import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import ErrorPopup from "../../components/elements/ErrorPopup";
import Loader from "../../components/elements/Loader";
import SnackbarAlert from "../../components/elements/SnackbarAlert";
import useLandingAuthApi from "../../services/api/useLandingAuthApi";
import PaginationComponent from "../ListOfTripRequest/views/PaginationComponent";
import SearchBar from "../ListOfTripRequest/views/SearchBar";
import "./css/UnregisteredAircraftTripsPage.css";
import UnregisteredAircraftTripsHeader from "./UnregisteredAircraftTripsHeader";
import UnregisteredAircraftTripsTable from "./views/UnregisteredAircraftTripsTable";
import ExportDateRangeModal from "../../components/elements/ExportDateRangeModal";

const UnregisteredAircraftTripsPage = () => {
  const { fetchUnregisteredAircraftTrips, searchUnregisteredAircraftTrips, exportUnregisteredAircraftTrips } =
    useLandingAuthApi();

  const location = useLocation();
  const initialPage = parseInt(location.state?.currentPage) || 1;
  const [currentPage, setCurrentPage] = useState(initialPage);
  const itemsPerPage = 10;
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportSuccessMessage, setExportSuccessMessage] = useState("");

  // Debounce search term - only update if empty or > 3 characters
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmedSearch = searchTerm.trim();
      setDebouncedSearchTerm((prevDebounced) => {
        // If search is empty or > 3 characters, update debouncedSearchTerm
        if (trimmedSearch.length === 0 || trimmedSearch.length > 3) {
          // Reset to first page when search actually changes
          if (prevDebounced !== trimmedSearch) {
            setCurrentPage(1);
          }
          return trimmedSearch;
        }
        // If search is 1-3 characters and previous was > 3, reset to empty
        // This handles case when user deletes from 4+ chars to 1-3 chars
        if (trimmedSearch.length > 0 && trimmedSearch.length <= 3 && prevDebounced && prevDebounced.length > 3) {
          setCurrentPage(1);
          return "";
        }
        // Otherwise, keep previous value (don't trigger API call for 1-3 chars)
        return prevDebounced;
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Only use search API if debounced search term length > 3
  const shouldUseSearch = debouncedSearchTerm && debouncedSearchTerm.trim().length > 3;

  const {
    data: aircraftData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "unregistered-aircraft-trips",
      currentPage,
      itemsPerPage,
      debouncedSearchTerm,
    ],
    queryFn: async () => {
      try {
        let result;
        if (shouldUseSearch) {
          // Use search API when debounced search term length > 3
          result = await searchUnregisteredAircraftTrips(
            debouncedSearchTerm.trim(),
            currentPage - 1,
            itemsPerPage
          );
        } else {
          // Use regular fetch API when no search term
          result = await fetchUnregisteredAircraftTrips(
            currentPage - 1,
            itemsPerPage
          );
        }
        return result;
      } catch (error) {
        if (error.status === 404) {
          return null;
        }
        throw error;
      }
    },
    keepPreviousData: true,
    retry: false,
  });

  let aircraftList = [];
  if (aircraftData) {
    if (aircraftData?.data?.content) {
      aircraftList = aircraftData.data.content || [];
    }
  }

  const totalPages = aircraftData?.data?.totalPages || 1;
  const totalElements = aircraftData?.data?.totalElements || 0;
  const cumulativeCount = Math.min(currentPage * itemsPerPage, totalElements);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = (term) => {
    if (term) {
      setCurrentPage(1);
    }
    setSearchTerm(term);
  };

  const handleQrScanSuccess = async (arn) => {
    // Handle QR scan for ARN search
    setSearchTerm(arn);
    setCurrentPage(1);
  };

  const handleDownloadClick = () => {
    setIsExportModalOpen(true);
  };

  const exportTripsMutation = useMutation({
    mutationFn: ({ fromDate, toDate }) =>
      exportUnregisteredAircraftTrips(fromDate, toDate),
    onSuccess: (response) => {
      // Show the message from response.data
      const message = response?.data;
      setExportSuccessMessage(message);
      setIsExportModalOpen(false);
      // Clear the message after 6 seconds (matching SnackbarAlert autoHideDuration)
      setTimeout(() => {
        setExportSuccessMessage("");
      }, 6000);
    },
    onError: (error) => {
      console.error("Export error:", error);
      setIsExportModalOpen(false);
    },
  });

  const handleExportConfirm = (fromDate, toDate) => {
    // Format dates as YYYY/MM/DD for the API
    const formattedFromDate = dayjs(fromDate).format("YYYY-MM-DD");
    const formattedToDate = dayjs(toDate).format("YYYY-MM-DD");
    exportTripsMutation.mutate({
      fromDate: formattedFromDate,
      toDate: formattedToDate,
    });
  };

  const handleExportCancel = () => {
    setIsExportModalOpen(false);
  };

  return (
    <div className="unregistered-aircraft-trips-page">
      <UnregisteredAircraftTripsHeader />
      {exportSuccessMessage && (
        <SnackbarAlert type="success">{exportSuccessMessage}</SnackbarAlert>
      )}
      {exportTripsMutation.isError && (
        <SnackbarAlert type="error">
          {exportTripsMutation.error?.message || "Failed to export trips"}
        </SnackbarAlert>
      )}
      <SearchBar
          onSearch={handleSearch}
          onQrScanSuccess={handleQrScanSuccess}
          showQrScan={false}
          value={searchTerm}
          placeholder="Search by Aircraft Reg. No."
      />
      {aircraftList.length > 0 && (
        <div className="unregistered-aircraft-trips-details-controls">
          <button
            className="unregistered-aircraft-trips-download-button"
            onClick={handleDownloadClick}
          >
            Download
          </button>
        </div>
      )}
     
      {error && error.status !== 404 && (
        <div className="trip-request-error-message">
          <ErrorPopup children={error.message}></ErrorPopup>
        </div>
      )}
      {error && error.status === 404 && (
        <div className="no-trips-message-container">
          No unregistered aircraft trips available
        </div>
      )}
      {!error && (
        <>
          {isLoading && <Loader />}
          {aircraftList.length === 0 && !isLoading && (
            <div className="no-trips-message-container">
              No aircraft available
            </div>
          )}
          {aircraftList.length > 0 && !isLoading && (
            <UnregisteredAircraftTripsTable
              aircraftList={aircraftList}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
            />
          )}
        </>
      )}
      {aircraftList.length > 0 && !isLoading && totalPages > 1 && (
        <PaginationComponent
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
      <ExportDateRangeModal
        isOpen={isExportModalOpen}
        onClose={handleExportCancel}
        onConfirm={handleExportConfirm}
        isLoading={exportTripsMutation.isPending}
      />
    </div>
  );
};

export default UnregisteredAircraftTripsPage;
